import {
  DdFlagType,
  DdWithCaseClustersToConsider,
  CitationsFragment,
  QualifiedFeature,
  QualifiedFeatureFragment,
  SortType,
} from "../graphql";
import { useMemo } from "react";
import { DDClinicalReasoning } from "../graphql/dao";
import { uniq } from "lodash";

const getOrderedCrs = (crIds, crById) => crIds.map((crId) => crById[crId]);
const ORPHANS_CLUSTER_ID = "FE.cb147b35-ab4c-403e-8215-d4f7008a453f";

export enum DdFlagTypeExt {
  Added = "ADDED",
  Removed = "REMOVED",
}

export type DiseaseFlag = DdFlagType | DdFlagTypeExt;

export type DiseaseCluster = {
  qf: QualifiedFeatureFragment;
  crs: DDClinicalReasoning[];
  flags?: DiseaseFlag[];
  citations: CitationsFragment;
  normalizedStrength?: number;
};

export enum DDSortType {
  BySuspicionIndex = "BySuspicionIndex",
  ByOrganSystem = "ByOrganSystem",
}

export type DDSection = { title: string; elements: DiseaseCluster[] };

function computePlanSections(
  sortedFeaturesWithCrs: DiseaseCluster[],
  strength: number,
  sortType: SortType,
  diseasesToIncludeInPlan: QualifiedFeature[],
  diseasesToRemoveFromPlan: QualifiedFeature[],
  diseasesToAdd: QualifiedFeature[],
): DDSection[] {
  let topDiseases = [];
  let restOfDiseases = [];

  const diseasesToAddIDs = diseasesToAdd?.map((qf) => qf.feature.id) || [];

  sortedFeaturesWithCrs.forEach((fcrs) => {
    const inTop = fcrs.crs[0].inTopCRPerSortType?.includes(sortType);
    let group = inTop ? topDiseases : restOfDiseases;
    if (diseasesToAddIDs.includes(fcrs.qf.feature.id)) {
      fcrs.flags.push(DdFlagTypeExt.Added);
    }
    group.push(fcrs);
  });

  if (diseasesToAdd && diseasesToAdd.length > 0) {
    //If there is no reasoning for a disease that client added, add it
    const diseasesToAddWithSubtypes = diseasesToAdd
      .filter((d) =>
        topDiseases.every((td) => td.qf.feature.id !== d.feature.id),
      )
      .map((d) => {
        return {
          qf: d,
          flags: [DdFlagTypeExt.Added],
          crs: [{ disease: [d] }],
        };
      });

    topDiseases = [...topDiseases, ...diseasesToAddWithSubtypes];
  }

  // Return two groups - in plan, and excluded from plan
  return [
    { title: "Most Likely", elements: topDiseases },
    { title: "Less Likely", elements: restOfDiseases },
  ];
}

function computeOrganSections<T>(
  sortedFeaturesWithCrs: DiseaseCluster[],
): DDSection[] {
  const sections = [];

  const allOrganSystems = sortedFeaturesWithCrs.map(
    (fcrs) => fcrs.qf.feature.organSystem,
  );
  const uniqueOrganSystems = uniq(
    allOrganSystems.filter((organSystem) => organSystem !== null),
  );
  uniqueOrganSystems.forEach((organSystem) => {
    sections.push({
      title: organSystem.name,
      elements: sortedFeaturesWithCrs.filter(
        (fcrs) =>
          fcrs.qf.feature.organSystem &&
          fcrs.qf.feature.organSystem.shortname === organSystem.shortname,
      ),
    });
  });
  return sections;
}

export const useSortedFeaturesWithCrs = (
  crs: DDClinicalReasoning[],
  clustersToConsider: DdWithCaseClustersToConsider[],
  sortType: SortType,
  strength: number,
  ddSortType: DDSortType = DDSortType.BySuspicionIndex,
  diseasesToIncludeInPlan: QualifiedFeature[] = null,
  diseasesToRemoveFromPlan: QualifiedFeature[] = null,
  diseasesToAdd: QualifiedFeature[] = null,
): DDSection[] => {
  const crById = useMemo(
    () =>
      crs
        ? crs.reduce((acc, item) => {
            acc[item.id] = item;
            return acc;
          }, {})
        : {},
    [crs],
  );

  const sortedFeaturesWithCrs = useMemo(
    () =>
      clustersToConsider
        ? clustersToConsider
            .filter(
              (cluster) => cluster.title?.feature?.id !== ORPHANS_CLUSTER_ID,
            ) //avoid showing "unclustered" diseases in agent
            .filter((cr) => cr.sortType === sortType)
            .map((cr) => ({
              qf: cr.title,
              crs: getOrderedCrs(cr.crIds, crById).filter((cr) => cr),
              flags: cr.flags,
              citations: cr.citations,
              normalizedStrength: cr.scores.adjustedNormalized,
            }))
        : [],
    [clustersToConsider, crById, sortType],
  );

  return useMemo(
    () =>
      ddSortType === DDSortType.BySuspicionIndex
        ? computePlanSections(
            sortedFeaturesWithCrs,
            strength,
            sortType,
            diseasesToIncludeInPlan,
            diseasesToRemoveFromPlan,
            diseasesToAdd,
          )
        : computeOrganSections(sortedFeaturesWithCrs),
    [
      sortedFeaturesWithCrs,
      ddSortType,
      diseasesToIncludeInPlan,
      diseasesToRemoveFromPlan,
      diseasesToAdd,
    ],
  );
};

export const getFeaturesCountFromSections = <T>(
  featuresSections: DDSection[],
) => featuresSections.reduce((acc, item) => acc + item.elements.length, 0);
